import { useState, useEffect, useRef } from 'react';
import { MainLayout } from '../../../core/layouts/MainLayout';
import { FilterSidebar } from '../components/FilterSidebar';
import { SearchPageBar } from '../components/SearchPageBar';
import { SearchResults } from '../components/SearchResults';
import { useSearch } from '../hooks/useSearch';
import { useTranslation } from '../../../core/hooks/useTranslation';
import { SearchUnit } from '../components/SearchResultCard';
import { useSearchParams } from 'react-router-dom';
import { MapView } from '../components/MapView';
import { List } from 'lucide-react';

export const SearchPage = () => {
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMapView = searchParams.get('map') === 'true';

    const toggleMapView = () => {
        if (isMapView) {
            searchParams.delete('map');
        } else {
            searchParams.set('map', 'true');
        }
        setSearchParams(searchParams);
    };

    // Custom hook to handle search logic
    const { results, filters, updateFilters, totalResults, totalPages, isLoading } = useSearch();

    // Infinite Scroll State (Mobile)
    const [allResults, setAllResults] = useState<SearchUnit[]>([]);
    const observerTarget = useRef<HTMLDivElement>(null);

    // Reset accumulated results when filters change (except page)
    useEffect(() => {
        if (filters.page === 1) {
            setAllResults(results);
        } else {
            setAllResults(prev => [...prev, ...results]);
        }
    }, [results, filters.page]);

    // Handle initial scroll top
    useEffect(() => {
        if (filters.page === 1) {
            window.scrollTo(0, 0);
        }
    }, [filters.page]);

    // Infinite Scroll Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && !isLoading && filters.page < totalPages) {
                    updateFilters('page', filters.page + 1);
                }
            },
            { threshold: 1.0 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => observer.disconnect();
    }, [isLoading, totalPages, filters.page, updateFilters]);

    const handleSearch = (query: string) => {
        // Only update 'q' in filters without dropping 'map' state
        if (query) {
            setSearchParams(prev => {
                prev.set('q', query);
                // Also reset pagination logically
                prev.delete('page');
                return prev;
            });
        } else {
            setSearchParams(prev => {
                prev.delete('q');
                prev.delete('page');
                return prev;
            });
        }
    };

    return (
        <MainLayout forceSolidNavbar hideFooter={isMapView}>
            <div className={`bg-gray-50/50 ${isMapView ? 'h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] w-screen overflow-hidden fixed top-16 sm:top-20 inset-0' : 'min-h-screen'}`}>

                {/* 
                  FULL-SCREEN MAP VIEW MODE
                  ===================================================
                */}
                {isMapView ? (
                    <div className="relative w-full h-full">
                        {/* Background Map View */}
                        <div className="absolute inset-0 z-0">
                            <MapView units={isMobileFiltersOpen ? allResults : results} projects={[]} />
                        </div>

                        {/* Floating Top Search Bar */}
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-full max-w-3xl z-40 px-4 flex flex-col gap-2 items-center">
                            <div className="p-1 w-full">
                                <SearchPageBar
                                    onSearch={handleSearch}
                                    totalResults={totalResults}
                                    onFilterClick={() => setIsMobileFiltersOpen(true)}
                                    sort={filters.sort}
                                    onSortChange={(val) => updateFilters('sort', val)}
                                    isMapView={isMapView}
                                    onToggleMapView={toggleMapView}
                                />
                            </div>

                            {/* Toggle Button for List View (Now under SearchBar) */}
                            <button onClick={toggleMapView} className="bg-secondary text-white px-5 py-2 text-sm font-bold shadow-lg rounded-full hover:bg-secondary-dark flex items-center gap-2 transition-transform active:scale-95 border-2 border-white/20 whitespace-nowrap self-center pointer-events-auto">
                                <List className="w-4 h-4" />
                                {t('search.toggle.list', 'List View')}
                            </button>
                        </div>

                        {/* Floating Desktop Sidebar (Optional/On Demand) */}
                        {isMobileFiltersOpen && (
                            <div className="hidden lg:block absolute top-24 left-4 lg:left-8 z-40 w-[320px] max-h-[calc(100vh-8rem)] overflow-y-auto bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white p-4 custom-scrollbar">
                                <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-100">
                                    <h2 className="font-bold text-lg">{t('search.filters.label', 'Filters')}</h2>
                                    <button onClick={() => setIsMobileFiltersOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">✕</button>
                                </div>
                                <FilterSidebar
                                    filters={filters}
                                    onUpdate={updateFilters}
                                    isOpen={true}
                                    onClose={() => setIsMobileFiltersOpen(false)}
                                    className="!w-full md:!w-full"
                                />
                            </div>
                        )}

                        {/* Mobile Sidebar Overlay */}
                        <div className="lg:hidden block">
                            <FilterSidebar
                                filters={filters}
                                onUpdate={updateFilters}
                                isOpen={isMobileFiltersOpen}
                                onClose={() => setIsMobileFiltersOpen(false)}
                            />
                        </div>
                    </div>
                ) : (
                    /* 
                      STANDARD LIST VIEW MODE
                      ===================================================
                    */
                    <div className="container mx-auto px-4 md:px-12 lg:px-24 pt-24 md:pt-28 pb-12">
                        <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                            {/* Left Sidebar (Filters) */}
                            <aside className={`
                            lg:w-1/4 lg:block fixed lg:static inset-0 z-50 bg-white lg:bg-transparent
                            transition-transform duration-300 transform
                            ${isMobileFiltersOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                        `}>
                                {/* Mobile Sidebar Header */}
                                <div className="lg:hidden p-4 border-b border-gray-100 flex justify-between items-center bg-white">
                                    <h2 className="font-bold text-lg">{t('search.filters.label', 'Filters')}</h2>
                                    <button onClick={() => setIsMobileFiltersOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">✕</button>
                                </div>

                                <div className="lg:sticky lg:top-28 lg:h-[calc(100vh-8rem)] overflow-y-auto bg-white lg:bg-transparent p-4 lg:p-0 scrollbar-hide">
                                    <FilterSidebar
                                        filters={filters}
                                        onUpdate={updateFilters}
                                        isOpen={isMobileFiltersOpen}
                                        onClose={() => setIsMobileFiltersOpen(false)}
                                    />
                                </div>
                            </aside>

                            {/* Right Content (Results) */}
                            <main className="w-full lg:w-3/4">
                                {/* Sticky Search Header Mobile Wrapper */}
                                <div className="sticky top-16 md:static z-20 bg-gray-50/95 backdrop-blur-sm md:bg-transparent py-2 md:py-0 -mx-4 px-4 md:mx-0 md:px-0">
                                    <SearchPageBar
                                        onSearch={handleSearch}
                                        totalResults={totalResults}
                                        onFilterClick={() => setIsMobileFiltersOpen(true)}
                                        sort={filters.sort}
                                        onSortChange={(val) => updateFilters('sort', val)}
                                        isMapView={isMapView}
                                        onToggleMapView={toggleMapView}
                                    />
                                </div>

                                {/* Mobile Results (Infinite Scroll) */}
                                <div className="md:hidden">
                                    <SearchResults results={allResults} isLoading={isLoading && filters.page === 1} />
                                    {isLoading && filters.page > 1 && (
                                        <div className="py-4 text-center">
                                            <div className="inline-block w-6 h-6 border-2 border-secondary border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                    <div ref={observerTarget} className="h-4 w-full"></div>
                                </div>

                                {/* Desktop Results (Pagination) */}
                                <div className="hidden md:block">
                                    <SearchResults results={results} isLoading={isLoading} />

                                    {/* Pagination */}
                                    {!isLoading && totalPages > 1 && (
                                        <div className="mt-8 flex justify-center gap-2 pb-8">
                                            <button
                                                onClick={() => updateFilters('page', filters.page - 1)}
                                                disabled={filters.page === 1}
                                                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                                            >
                                                ←
                                            </button>
                                            {[...Array(totalPages)].map((_, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => updateFilters('page', i + 1)}
                                                    className={`
                                                    w-10 h-10 rounded-lg flex items-center justify-center font-medium transition-colors
                                                    ${filters.page === i + 1
                                                            ? 'bg-secondary text-white'
                                                            : 'bg-white border hover:bg-gray-50 text-gray-700'}
                                                `}
                                                >
                                                    {i + 1}
                                                </button>
                                            ))}
                                            <button
                                                onClick={() => updateFilters('page', filters.page + 1)}
                                                disabled={filters.page === totalPages}
                                                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                                            >
                                                →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </main>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};
